import bcrypt from "bcrypt";
import {
  redirect,
  Form,
  useActionData,
  type ActionFunctionArgs,
  useSearchParams,
  Link,
  type LoaderFunctionArgs,
  type MetaFunction,
  useParams,
} from "react-router";
import {Button, Checkbox, Image, Paper, Text, TextInput, Title, PasswordInput} from "@mantine/core";

import {CsrfToken} from "@digisthesia/remix-utils/csrf-token";
import {safeFormData} from "@digisthesia/remix-utils/helpers.server";

import {createUser} from "~/auth.server";
import {getUserCookie} from "~/context.server";
import {getGlobalStrings, resolveLocale, resolveLocaleFromRequest, useGlobalStrings} from "~/strings/global";
import {getLangPrefix} from "~/routes/route-helpers";

function safeReturnTo(url: string | null) {
  return url && url.startsWith("/") ? url : "/";
}

export async function loader({request}: LoaderFunctionArgs) {
  return {locale: resolveLocaleFromRequest(request)};
}

export async function action({request, url}: ActionFunctionArgs) {
  const globalStrings = getGlobalStrings(resolveLocaleFromRequest(request));
  const formData = await safeFormData({request});
  const locale = resolveLocaleFromRequest(request);
  const email = formData.get("email") as string;
  const password = formData.get("password") as string;
  const passwordConfirmation = formData.get("passwordConfirmation") as string;
  const newsletterSubscribed = formData.get("newsletterSubscribed") === "on";

  if (password !== passwordConfirmation) {
    return {email, error: globalStrings.signupPasswordMismatch};
  }

  const hashedPassword = await bcrypt.hash(password, await bcrypt.genSalt());

  try {
    const id = await createUser({email, password: hashedPassword, newsletterSubscribed, locale});
    throw redirect(safeReturnTo(url.searchParams.get("return_to")), {
      headers: {"set-cookie": await getUserCookie().serialize(id)},
    });
  } catch (err: any) {
    if (err instanceof Response) {
      throw err;
    }
    if (err instanceof Error && err.message.includes("Un compte existe déjà")) {
      return {email, error: globalStrings.signupEmailExists};
    }
    return {email, error: err instanceof Error ? err.message : globalStrings.signupGenericError};
  }
}

export default function Signup() {
  const globalStrings = useGlobalStrings();
  const {error, email} = useActionData<typeof action>() ?? {};
  const [searchParams] = useSearchParams();
  const {lang} = useParams();
  const langPrefix = getLangPrefix(lang);
  return (
    <div className="login-wrapper">
      <Paper className="login-form">
        <Image mx="auto" w={200} src="http://static.digisthesia.com/hexploit/images/logo_hexploit_500.webp" />

        <Title order={2} ta="center">
          {globalStrings.signupHeading}
        </Title>

        <Form method="POST">
          <CsrfToken />
          {error ? <p>{error}</p> : null}

          <TextInput
            defaultValue={email ?? searchParams.get("email") ?? ""}
            label={globalStrings.signupEmailLabel}
            id="email"
            name="email"
            type="email"
            placeholder={globalStrings.signupEmailPlaceholder}
            size="md"
            radius="md"
          />
          <PasswordInput
            label={globalStrings.signupPasswordLabel}
            placeholder={globalStrings.signupPasswordPlaceholder}
            id="password"
            name="password"
            mt="md"
            size="md"
            radius="md"
          />
          <PasswordInput
            placeholder={globalStrings.signupPasswordConfirmationPlaceholder}
            id="passwordConfirmation"
            name="passwordConfirmation"
            mt="md"
            size="md"
            radius="md"
          />

          <Checkbox
            id="newsletterSubscribed"
            name="newsletterSubscribed"
            label={globalStrings.signupNewsletterLabel}
            mt="md"
          />

          <Button fullWidth mt="xl" size="md" radius="md" type="submit">
            {globalStrings.signupSubmit}
          </Button>
        </Form>

        <Text ta="center" mt="md">
          {globalStrings.signupHaveAccount} <Link to={`${langPrefix}/login`}>{globalStrings.signupLoginLink}</Link>
        </Text>
      </Paper>
    </div>
  );
}

export const meta: MetaFunction<typeof loader> = ({loaderData}) => {
  const globalStrings = getGlobalStrings(resolveLocale(loaderData?.locale));
  return [{title: globalStrings.signupTitle}];
};
